import { motion } from "framer-motion";
import { TrendingDown, TrendingUp, Sparkles } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 30 — The Commercial Reality.
 * Four metrics moving in their "good" direction simultaneously, then the punchline:
 * "Normally those things don't happen together. Now they do."
 */

const metrics = [
  {
    label: "Cost per edit",
    direction: "down",
    color: "#96C11F",
    note: "Drops dramatically",
  },
  {
    label: "Output volume",
    direction: "up",
    color: "#00deb6",
    note: "Increases",
  },
  {
    label: "Turnaround time",
    direction: "down",
    color: "#96C11F",
    note: "Shrinks",
  },
  {
    label: "Product value",
    direction: "up",
    color: "#00deb6",
    note: "Increases",
  },
];

export default function Scene30_CommercialReality() {
  return (
    <SceneShell variant="forest" align="left" data-testid="scene-commercial-reality">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 25% 35%, rgba(150,193,31,0.18) 0%, transparent 55%), radial-gradient(ellipse at 75% 65%, rgba(0,222,182,0.14) 0%, transparent 55%)",
            "radial-gradient(ellipse at 75% 35%, rgba(0,222,182,0.18) 0%, transparent 55%), radial-gradient(ellipse at 25% 65%, rgba(150,193,31,0.14) 0%, transparent 55%)",
            "radial-gradient(ellipse at 25% 35%, rgba(150,193,31,0.18) 0%, transparent 55%), radial-gradient(ellipse at 75% 65%, rgba(0,222,182,0.14) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-4xl sm:text-5xl lg:text-6xl text-white"
          data-testid="commercial-reality-title"
        >
          Four metrics ·{" "}
          <span
            style={{
              background:
                "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              fontWeight: 900,
            }}
          >
            all moving the right way.
          </span>
        </motion.h2>

        {/* 2x2 grid of directional metrics */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {metrics.map((m, i) => {
            const Arrow = m.direction === "up" ? TrendingUp : TrendingDown;
            return (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 24, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.9,
                  delay: 0.6 + i * 0.18,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="rounded-2xl glass p-7 relative overflow-hidden"
                data-testid={`commercial-metric-${i}`}
              >
                <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                  <div
                    className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full opacity-25"
                    style={{
                      background: `radial-gradient(circle, ${m.color} 0%, transparent 70%)`,
                      filter: "blur(20px)",
                    }}
                  />
                </div>
                <div className="relative">
                  <div
                    className="text-[11px] tracking-[0.32em] uppercase mb-4"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                  >
                    {m.label}
                  </div>
                  <motion.div
                    initial={{ rotate: m.direction === "up" ? 30 : -30, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    transition={{
                      delay: 0.9 + i * 0.18,
                      duration: 0.7,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="flex items-center gap-3"
                  >
                    <Arrow
                      className="w-12 h-12"
                      style={{ color: m.color }}
                      strokeWidth={3}
                    />
                    <div
                      className="kn-display text-3xl sm:text-4xl"
                      style={{ color: m.color, fontWeight: 800 }}
                    >
                      {m.direction === "up" ? "↑" : "↓"}
                    </div>
                  </motion.div>
                  <div
                    className="mt-4 text-base"
                    style={{ color: "rgba(255,255,255,0.75)", fontWeight: 600 }}
                  >
                    {m.note}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* All four · at the same time pill */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.8, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex justify-center"
          data-testid="commercial-punchline"
        >
          <div className="inline-flex items-center gap-3 rounded-full px-5 py-3 glass">
            <Sparkles className="w-4 h-4" style={{ color: "#96C11F" }} />
            <span className="text-sm tracking-[0.18em] uppercase text-white/85">
              All four · at the same time
            </span>
          </div>
        </motion.div>
      </div>
    </SceneShell>
  );
}
